import { useTranslation } from "react-i18next"

import type {
  ContractTermAnalysis,
  ContractTermPosition,
  TimelineAlignment,
  TimelineCheck,
} from "../../types"

import { Check, Clock, XCircle } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Skeleton } from "@/components/ui/skeleton"
import { cn } from "@/lib/utils"

import { CitationText, type ExtractionItemLookup, type OpenSourceFn } from "./extraction-citation"

// ── Types ─────────────────────────────────────────────────────────────────────

type RiskTabProps = {
  contractAnalyses?: ContractTermAnalysis[]
  timelineChecks?: TimelineCheck[]
  isLoading: boolean
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}

// ── Helpers ───────────────────────────────────────────────────────────────────

const POSITION_CONFIG: Record<ContractTermPosition, { labelKey: string; badgeClass: string }> = {
  accepted: {
    labelKey: "tender.bidReview.riskTab.position.accepted",
    badgeClass: "border-success bg-success/10 text-success",
  },
  deviated: {
    labelKey: "tender.bidReview.riskTab.position.deviated",
    badgeClass: "border-destructive bg-destructive/10 text-destructive",
  },
  silent: {
    labelKey: "tender.bidReview.riskTab.position.silent",
    badgeClass: "border-warning bg-warning/10 text-warning-foreground",
  },
}

const RISK_LABEL: Record<string, { labelKey: string; class: string }> = {
  lag: {
    labelKey: "tender.bidReview.riskTab.contractRisk.lag",
    class: "border-success bg-success/10 text-success",
  },
  medel: {
    labelKey: "tender.bidReview.riskTab.contractRisk.medel",
    class: "border-warning bg-warning/10 text-warning-foreground",
  },
  hog: {
    labelKey: "tender.bidReview.riskTab.contractRisk.hog",
    class: "border-destructive bg-destructive/10 text-destructive",
  },
}

const ALIGNMENT_CONFIG: Record<
  TimelineAlignment,
  { icon: React.ElementType; iconClass: string; labelKey: string }
> = {
  aligned: {
    icon: Check,
    iconClass: "text-success dark:text-success",
    labelKey: "tender.bidReview.riskTab.alignment.aligned",
  },
  at_risk: {
    icon: Clock,
    iconClass: "text-warning dark:text-warning-foreground",
    labelKey: "tender.bidReview.riskTab.alignment.at_risk",
  },
  conflict: {
    icon: XCircle,
    iconClass: "text-destructive dark:text-destructive",
    labelKey: "tender.bidReview.riskTab.alignment.conflict",
  },
}

// ── Contract term row ─────────────────────────────────────────────────────────

function ContractTermRow({
  term,
  extractionLookup,
  onOpenSource,
}: {
  term: ContractTermAnalysis
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const positionConf = POSITION_CONFIG[term.bidPosition]
  const riskConf = RISK_LABEL[term.riskLevel]

  return (
    <div className="bg-muted/20 flex flex-col gap-1.5 rounded-lg border px-3 py-2.5">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[13px] font-medium">{term.clause}</span>
        <Badge className={cn("h-4 px-1.5 text-[10px]", positionConf.badgeClass)} variant="outline">
          {t(positionConf.labelKey)}
        </Badge>
        {term.bidPosition !== "accepted" && (
          <Badge className={cn("h-4 px-1.5 text-[10px]", riskConf.class)} variant="outline">
            {t(riskConf.labelKey)}
          </Badge>
        )}
      </div>
      {term.deviationDescription && (
        <p className="text-warning-foreground dark:text-warning-foreground text-[12px] leading-relaxed">
          {extractionLookup ? (
            <CitationText
              lookup={extractionLookup}
              text={term.deviationDescription}
              onOpenSource={onOpenSource}
            />
          ) : (
            term.deviationDescription
          )}
        </p>
      )}
      {term.suggestedAction && (
        <div className="bg-accent/10 text-accent-foreground dark:text-accent-foreground rounded-md px-2.5 py-2 text-[12px] leading-relaxed">
          <span className="font-medium">
            {t("tender.bidReview.framework.suggestedActionPrefix")}
          </span>
          {extractionLookup ? (
            <CitationText
              lookup={extractionLookup}
              text={term.suggestedAction}
              onOpenSource={onOpenSource}
            />
          ) : (
            term.suggestedAction
          )}
        </div>
      )}
    </div>
  )
}

// ── Timeline row ──────────────────────────────────────────────────────────────

function TimelineRow({
  check,
  extractionLookup,
  onOpenSource,
}: {
  check: TimelineCheck
  extractionLookup?: ExtractionItemLookup
  onOpenSource?: OpenSourceFn
}) {
  const { t } = useTranslation()
  const alignConf = ALIGNMENT_CONFIG[check.bidAlignment]
  const AlignIcon = alignConf.icon

  return (
    <div className="bg-muted/20 flex items-start gap-3 rounded-lg border px-3 py-2.5">
      <AlignIcon className={cn("mt-0.5 size-4 shrink-0", alignConf.iconClass)} />
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-[13px] font-medium">{check.event}</span>
          <span className="text-muted-foreground text-[12px]">{check.requiredDate}</span>
          <Badge
            variant="outline"
            className={cn(
              "h-4 px-1.5 text-[10px]",
              check.bidAlignment === "aligned" && "border-success bg-success/10 text-success",
              check.bidAlignment === "at_risk" &&
                "border-warning bg-warning/10 text-warning-foreground",
              check.bidAlignment === "conflict" &&
                "border-destructive bg-destructive/10 text-destructive"
            )}
          >
            {t(alignConf.labelKey)}
          </Badge>
        </div>
        {check.analysis && (
          <p className="text-muted-foreground mt-0.5 text-[12px] leading-relaxed">
            {extractionLookup ? (
              <CitationText
                lookup={extractionLookup}
                text={check.analysis}
                onOpenSource={onOpenSource}
              />
            ) : (
              check.analysis
            )}
          </p>
        )}
      </div>
    </div>
  )
}

// ── Loading skeleton ──────────────────────────────────────────────────────────

function LoadingSkeleton() {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-14 w-full" />
        <Skeleton className="h-14 w-full" />
      </div>
      <div className="flex flex-col gap-2">
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-12 w-full" />
      </div>
    </div>
  )
}

// ── Main component ────────────────────────────────────────────────────────────

export function RiskTab({
  contractAnalyses,
  timelineChecks,
  isLoading,
  extractionLookup,
  onOpenSource,
}: RiskTabProps) {
  const { t } = useTranslation()
  if (isLoading) {
    return <LoadingSkeleton />
  }

  const hasContracts = (contractAnalyses?.length ?? 0) > 0
  const hasTimeline = (timelineChecks?.length ?? 0) > 0

  if (!hasContracts && !hasTimeline) {
    return (
      <p className="text-muted-foreground py-8 text-center text-[13px]">
        {t("tender.bidReview.riskTab.empty")}
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-6">
      {hasContracts && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.riskTab.contractTerms")}
          </h4>
          <div className="flex flex-col gap-2">
            {contractAnalyses!.map((term) => (
              <ContractTermRow
                key={term.termId}
                extractionLookup={extractionLookup}
                term={term}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}

      {hasTimeline && (
        <section>
          <h4 className="mb-2 text-[12px] font-semibold tracking-wide uppercase">
            {t("tender.bidReview.riskTab.timeline")}
          </h4>
          <div className="flex flex-col gap-2">
            {timelineChecks!.map((check) => (
              <TimelineRow
                key={check.dateId}
                check={check}
                extractionLookup={extractionLookup}
                onOpenSource={onOpenSource}
              />
            ))}
          </div>
        </section>
      )}
    </div>
  )
}
